import React from 'react';
import {
  getFlyerPlaceholderDate,
  getFlyerPlaceholderGenre,
  getFlyerPlaceholderInitials,
  getFlyerPlaceholderTitle,
  getFlyerPlaceholderVenue,
  resolveFlyerPlaceholderTheme,
} from '../../utils/flyerPlaceholderTheme';

const clampTextStyle = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-word',
};

const renderDecoration = (designKey, accent) => {
  const commonStyle = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    color: accent,
    pointerEvents: 'none',
  };
  switch (designKey) {
    case 'strings':
      return (
        <svg viewBox="0 0 200 280" aria-hidden="true" style={{ ...commonStyle, opacity: 0.2 }}>
          {Array.from({ length: 9 }).map((_, index) => (
            <line
              key={index}
              x1={16 + index * 18}
              y1="0"
              x2={58 + index * 16}
              y2="280"
              stroke="currentColor"
              strokeWidth="0.9"
            />
          ))}
        </svg>
      );
    case 'ornament':
      return (
        <svg viewBox="0 0 200 280" aria-hidden="true" style={{ ...commonStyle, opacity: 0.22 }}>
          <circle cx="40" cy="42" r="74" stroke="currentColor" strokeWidth="0.8" fill="none" />
          <circle cx="40" cy="42" r="56" stroke="currentColor" strokeWidth="0.8" fill="none" />
          <line x1="40" y1="-34" x2="40" y2="120" stroke="currentColor" strokeWidth="0.8" />
          <line x1="-36" y1="42" x2="116" y2="42" stroke="currentColor" strokeWidth="0.8" />
        </svg>
      );
    case 'abstract-flow':
      return (
        <svg viewBox="0 0 200 280" aria-hidden="true" style={{ ...commonStyle, opacity: 0.14 }}>
          <path d="M-20 170 Q 48 128, 102 170 T 222 170" fill="none" stroke="currentColor" strokeWidth="1.2" />
          <path d="M-20 186 Q 52 144, 106 186 T 222 186" fill="none" stroke="currentColor" strokeWidth="1.2" />
          <path d="M-20 202 Q 56 160, 110 202 T 222 202" fill="none" stroke="currentColor" strokeWidth="1.2" />
          <path d="M-20 218 Q 60 176, 114 218 T 222 218" fill="none" stroke="currentColor" strokeWidth="1.2" />
        </svg>
      );
    case 'piano-keys':
      return (
        <svg viewBox="0 0 200 280" aria-hidden="true" style={{ ...commonStyle, opacity: 0.14 }}>
          {Array.from({ length: 12 }).map((_, index) => (
            <rect
              key={index}
              x={index * 17}
              y="170"
              width="14"
              height="110"
              fill="currentColor"
            />
          ))}
        </svg>
      );
    case 'minimal-circle':
      return (
        <svg viewBox="0 0 200 280" aria-hidden="true" style={{ ...commonStyle, opacity: 0.08 }}>
          <circle cx="108" cy="118" r="94" fill="currentColor" />
        </svg>
      );
    case 'leaf':
      return (
        <svg viewBox="0 0 200 280" aria-hidden="true" style={{ ...commonStyle, opacity: 0.16 }}>
          <path d="M114 248 Q 118 160, 166 88" stroke="currentColor" strokeWidth="2.2" fill="none" />
          <path d="M114 248 Q 110 154, 54 92" stroke="currentColor" strokeWidth="2.2" fill="none" />
          <path d="M120 226 Q 148 176, 170 106" stroke="currentColor" strokeWidth="1" fill="none" />
          <path d="M108 226 Q 82 176, 56 110" stroke="currentColor" strokeWidth="1" fill="none" />
        </svg>
      );
    default:
      return null;
  }
};

const pickFirstString = (...values) =>
  values.find((value) => typeof value === 'string' && value.trim()) || '';

const hasResolvedFlyerAsset = (event = {}) =>
  Boolean(
    pickFirstString(
      event?.flyer_front_url,
      event?.flyerFrontUrl,
      event?.flyer_back_url,
      event?.flyerBackUrl,
      event?.flyer_url,
      event?.flyerUrl,
      event?.flyer_front_source_event_uuid,
      event?.flyerFrontSourceEventUuid,
      event?.flyer_back_source_event_uuid,
      event?.flyerBackSourceEventUuid
    )
  );

const formatShortTime = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.slice(0, 5);
};

const formatMonthDayLabel = (value) => {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return `${Number(match[2])}/${Number(match[3])}`;
  }
  return raw;
};

const buildSmallPlaceholderLines = (title, performanceLabel) => {
  const safeTitle = String(title || '').trim();
  const safePerformance = String(performanceLabel || '').trim();
  if (safePerformance) {
    return {
      titleLine: safeTitle || 'Untitled Event',
      detailLine: safePerformance,
      titleClamp: 1,
      detailClamp: 1,
    };
  }
  return {
    titleLine: safeTitle || 'Untitled Event',
    detailLine: '',
    titleClamp: 2,
    detailClamp: 0,
  };
};

const resolveSolidBackground = (colorKey, palette) => {
  switch (colorKey) {
    case 'midnight-symphony':
      return 'var(--flyer-placeholder-navy-deep)';
    case 'royal-bordeaux':
      return 'var(--red-900)';
    case 'dusty-rose':
      return 'var(--rose-200)';
    case 'oxford-navy':
      return 'var(--blue-900)';
    case 'forest-mist':
      return 'var(--emerald-800)';
    case 'deep-slate':
      return 'var(--flyer-placeholder-zinc)';
    case 'ivory-recital':
      return 'var(--flyer-placeholder-stone-soft)';
    case 'antique-cream':
      return 'var(--orange-100)';
    case 'noir-silver':
      return 'var(--flyer-placeholder-neutral-deep)';
    default:
      return palette?.accent || 'var(--gray-200)';
  }
};

const FLYER_PRESENT_PLACEHOLDER_BACKGROUND =
  'linear-gradient(180deg, var(--flyer-placeholder-present-start) 0%, var(--flyer-placeholder-present-end) 100%)';

const LargeFlyerPlaceholder = ({ palette, title, date, venue, genre, initials, designKey }) => (
  <>
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: '6%',
        border: `1px solid ${palette.frame}`,
        borderRadius: 'inherit',
      }}
    />
    {renderDecoration(designKey, palette.accent)}
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: palette.overlay,
        pointerEvents: 'none',
      }}
    />
    <div
      style={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '12% 10% 10%',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        {date ? (
          <div
            style={{
              marginBottom: '6%',
              fontSize: 'clamp(9px, 2.6vw, 12px)',
              letterSpacing: '0.42em',
              textTransform: 'uppercase',
              color: palette.mutedText,
              fontWeight: 700,
            }}
          >
            {date}
          </div>
        ) : null}
        <div
          style={{
            width: '24%',
            height: 1,
            margin: '0 auto 7%',
            background: palette.frame,
          }}
        />
        <div
          style={{
            ...clampTextStyle,
            WebkitLineClamp: 3,
            fontSize: 'clamp(18px, 6vw, 34px)',
            lineHeight: 1.18,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: palette.text,
            padding: '0 4%',
          }}
        >
          {title}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gap: '5%',
          justifyItems: 'center',
          textAlign: 'center',
        }}
      >
        {genre ? (
          <div
            style={{
              display: 'inline-flex',
              maxWidth: '100%',
              alignItems: 'center',
              gap: '0.55rem',
              fontSize: 'clamp(9px, 2.4vw, 11px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: palette.mutedText,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: palette.accent,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                ...clampTextStyle,
                WebkitLineClamp: 1,
              }}
            >
              {genre}
            </span>
          </div>
        ) : null}
        <div
          style={{
            fontSize: 'clamp(12px, 3.3vw, 16px)',
            lineHeight: 1.35,
            color: palette.text,
            letterSpacing: '0.03em',
            maxWidth: '82%',
            ...clampTextStyle,
            WebkitLineClamp: 2,
          }}
        >
          {venue || 'Flyer Coming Soon'}
        </div>
        <div
          style={{
            fontSize: 'clamp(9px, 2.4vw, 10px)',
            letterSpacing: '0.38em',
            textTransform: 'uppercase',
            color: palette.mutedText,
            fontWeight: 700,
          }}
        >
          {initials}
        </div>
      </div>
    </div>
  </>
);

const SmallFlyerPlaceholder = ({ palette, title, performanceLabel, date, designKey }) => {
  const lines = buildSmallPlaceholderLines(title, performanceLabel);

  return (
  <>
    {renderDecoration(designKey, palette.accent)}
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: palette.overlay,
        pointerEvents: 'none',
      }}
    />
    <div
      style={{
        position: 'absolute',
        top: '10%',
        left: '8%',
        right: '8%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '0.5rem',
      }}
    >
      <div
        style={{
          fontSize: 'clamp(7px, 1.85vw, 9px)',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: palette.mutedText,
          whiteSpace: 'nowrap',
          overflow: 'visible',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatMonthDayLabel(date) || 'NO IMAGE'}
      </div>
    </div>
    <div
      style={{
        position: 'absolute',
        left: '8%',
        right: '8%',
        bottom: '11%',
        display: 'grid',
        gap: 5,
      }}
    >
      <div
        style={{
          ...clampTextStyle,
          WebkitLineClamp: lines.titleClamp,
          fontSize: 'clamp(9px, 2.45vw, 12px)',
          lineHeight: 1.22,
          letterSpacing: '0.02em',
          color: palette.text,
          textTransform: 'uppercase',
          fontWeight: 700,
        }}
      >
        {lines.titleLine}
      </div>
      {lines.detailLine ? (
        <div
          style={{
            ...clampTextStyle,
            WebkitLineClamp: lines.detailClamp,
            fontSize: 'clamp(8px, 2.15vw, 10px)',
            lineHeight: 1.16,
            letterSpacing: '0.06em',
            color: palette.mutedText,
            textTransform: 'uppercase',
            fontWeight: 700,
          }}
        >
          {lines.detailLine}
        </div>
      ) : null}
    </div>
  </>
  );
};

const CardFlyerPlaceholder = ({
  palette,
  title,
  performanceLabel,
  date,
  openTime,
  startTime,
  designKey,
}) => (
  <>
    {renderDecoration(designKey, palette.accent)}
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: palette.overlay,
        pointerEvents: 'none',
      }}
    />
    <div
      style={{
        position: 'absolute',
        inset: '5.5%',
        border: `1px solid ${palette.frame}`,
        borderRadius: 'inherit',
      }}
    />
    <div
      style={{
        position: 'absolute',
        inset: '16% 10% 16%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        textAlign: 'center',
      }}
    >
      <div
        style={{
          ...clampTextStyle,
          WebkitLineClamp: 3,
          fontSize: 'clamp(11px, 3vw, 16px)',
          lineHeight: 1.18,
          letterSpacing: '0.05em',
          color: palette.text,
          textTransform: 'uppercase',
          fontWeight: 700,
        }}
      >
        {title}
      </div>
      {performanceLabel ? (
        <div
          style={{
            ...clampTextStyle,
            WebkitLineClamp: 2,
            fontSize: 'clamp(9px, 2.45vw, 12px)',
            lineHeight: 1.18,
            letterSpacing: '0.08em',
            color: palette.mutedText,
            textTransform: 'uppercase',
            fontWeight: 700,
          }}
        >
          {performanceLabel}
        </div>
      ) : null}
      {date ? (
        <div
          style={{
            fontSize: 'clamp(8px, 2.1vw, 10px)',
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: palette.mutedText,
            whiteSpace: 'nowrap',
            overflow: 'visible',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {date}
        </div>
      ) : null}
      {(openTime || startTime) ? (
        <div
          style={{
            display: 'inline-flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 6,
            fontSize: 'clamp(8px, 2.1vw, 10px)',
            lineHeight: 1.2,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: palette.text,
            fontWeight: 700,
          }}
        >
          {openTime ? (
            <span>
              <span style={{ color: palette.mutedText }}>Open </span>
              {openTime}
            </span>
          ) : null}
          {openTime && startTime ? (
            <span style={{ color: palette.frame }}>/</span>
          ) : null}
          {startTime ? (
            <span>
              <span style={{ color: palette.mutedText }}>Start </span>
              {startTime}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  </>
);

const FlyerPlaceholder = ({
  width = '100%',
  aspectRatio = '1 / 1.414',
  borderRadius = 12,
  className = '',
  style = {},
  event = null,
  eventId = '',
  eventTitle = '',
  eventDate = '',
  venue = '',
  genre = '',
  performanceType = '',
  designKey = '',
  colorKey = '',
  sizeVariant = 'large',
  visualMode = 'decorated',
  hasImage = false,
}) => {
  const normalizedEvent = event && typeof event === 'object'
    ? event
    : {
        event_uuid: eventId || '',
        name: eventTitle || '',
        event_date: eventDate || '',
        venue: venue || '',
        performance_type: performanceType || '',
        genreLabel: genre || '',
      };
  const theme = resolveFlyerPlaceholderTheme({
    event: normalizedEvent,
    eventId,
    designKey,
    colorKey,
  });
  const title = getFlyerPlaceholderTitle({
    ...normalizedEvent,
    eventTitle,
  });
  const date = getFlyerPlaceholderDate({
    ...normalizedEvent,
    eventDate,
  });
  const venueLabel = getFlyerPlaceholderVenue({
    ...normalizedEvent,
    venue,
  });
  const genreLabel = getFlyerPlaceholderGenre({
    ...normalizedEvent,
    genreLabel: genre || performanceType,
  });
  const performanceLabel = pickFirstString(
    normalizedEvent?.performance_type,
    normalizedEvent?.performanceType,
    performanceType
  );
  const openTime = formatShortTime(
    pickFirstString(normalizedEvent?.open_time, normalizedEvent?.openTime)
  );
  const startTime = formatShortTime(
    pickFirstString(normalizedEvent?.start_time, normalizedEvent?.startTime)
  );
  const initials = getFlyerPlaceholderInitials(title);
  const isSmall = sizeVariant === 'small';
  const isCard = sizeVariant === 'card';
  const shouldForceSolid =
    hasImage || (visualMode !== 'decorated' ? false : hasResolvedFlyerAsset(normalizedEvent));
  const resolvedVisualMode =
    visualMode === 'solid' || shouldForceSolid ? 'solid' : visualMode;
  const isSolid = resolvedVisualMode === 'solid';
  const backgroundStyle = isSolid
    ? (shouldForceSolid
        ? FLYER_PRESENT_PLACEHOLDER_BACKGROUND
        : resolveSolidBackground(theme.colorKey, theme.palette))
    : theme.palette.background;

  return (
    <div
      className={`flyer-placeholder-block${className ? ` ${className}` : ''}`}
      aria-hidden="true"
      style={{
        width,
        aspectRatio,
        borderRadius,
        background: backgroundStyle,
        display: 'block',
        position: 'relative',
        overflow: 'hidden',
        color: theme.palette.text,
        fontFamily: theme.palette.fontFamily,
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.03)',
        ...style,
      }}
    >
      {!isSolid && isCard ? (
        <CardFlyerPlaceholder
          palette={theme.palette}
          title={title}
          performanceLabel={performanceLabel}
          date={date}
          openTime={openTime}
          startTime={startTime}
          designKey={theme.designKey}
        />
      ) : !isSolid && isSmall ? (
        <SmallFlyerPlaceholder
          palette={theme.palette}
          title={title}
          performanceLabel={performanceLabel}
          date={date}
          designKey={theme.designKey}
        />
      ) : !isSolid ? (
        <LargeFlyerPlaceholder
          palette={theme.palette}
          title={title}
          date={date}
          venue={venueLabel}
          genre={genreLabel}
          initials={initials}
          designKey={theme.designKey}
        />
      ) : null}
    </div>
  );
};

export default FlyerPlaceholder;
export const flyerPlaceholderGradient = resolveFlyerPlaceholderTheme().palette.background;
